
// A reading the station may not send (null in the JSON) or that may not parse.
export type MaybeNumber = number | null | undefined;

export function hasValue(value: MaybeNumber): value is number {
    return value !== null && value !== undefined && !isNaN(value);
}

// Converts fahrenheit to celsius
export function fahToCelsius(fahrenheit: MaybeNumber) {
    return hasValue(fahrenheit) ? (fahrenheit - 32) * 5 / 9 : null;
}

// Converts mph to m/s
export function mphToMs(mph: MaybeNumber) {
    return hasValue(mph) ? mph * 0.44704 : null;
}

// Converts mph to km/h
export function mphToKmh(mph: MaybeNumber) {
    return hasValue(mph) ? mph * 1.60934 : null;
}

// Converts inHg to hPa
export function inHgToHpa(inHg: MaybeNumber) {
    return hasValue(inHg) ? inHg * 33.86389 : null;
}

// Converts inches to mm
export function inchesToMm(inches: MaybeNumber) {
    return hasValue(inches) ? inches * 25.4 : null;
}

// Next needle angle for wind direction `deg`, continuing from `current` the short way round
// (at 350, a new direction of 10 gives 370, not 10), so the needle never spins backwards.
export function nextContinuousAngle(current: number | null, deg: number) {
    if (current === null) {
        return deg;
    }

    const delta = ((deg - current) % 360 + 540) % 360 - 180;
    return current + delta;
}

// Converts degrees to one of 8 compass points (N, NE, E ... NW), using the given labels.
export function degToCardinal(deg: number, cardinals: string[]) {
    // Each point covers 45°, centred on it.
    return cardinals[Math.floor(deg / 45 + 0.5) % 8];
}


// Formats an ISO date as a local time ("13:26" / "1:26 PM"). Empty for missing or invalid dates.
export function formatTime(isoDate: string | null | undefined, locale: string, hour12: boolean): string {
    if (!isoDate) {
        return "";
    }

    const dateTime = new Date(isoDate);

    if (isNaN(dateTime.getTime())) {
        console.warn("Invalid date format: " + isoDate);
        return "";
    }

    return dateTime.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: 'numeric',
        hour12,
    });
}


// Formats a number rounded to the given decimals, dropping trailing zeros ("12.0" -> "12").
// Returns an em dash for missing values.
export function formatNumber(value: MaybeNumber, decimals = 0): string {
    if (!hasValue(value)) {
        return "—";
    }

    const factor = Math.pow(10, Math.max(0, decimals));
    const rounded = Math.round(value * factor) / factor;

    // Avoid "-0"
    return (rounded === 0 ? 0 : rounded).toString();
}

// Apparent ("feels like") temperature, Steadman / Australian BoM formula.
export function apparentTemperature(tempC: MaybeNumber, humidity: MaybeNumber, windMs: MaybeNumber) {
    if (!hasValue(tempC) || !hasValue(humidity) || !hasValue(windMs)) {
        return null;
    }

    const vapourPressure = humidity / 100 * 6.105 * Math.exp(17.27 * tempC / (237.7 + tempC));
    return tempC + 0.33 * vapourPressure - 0.70 * windMs - 4.00;
}

// UV index -> WHO risk band (0 = low ... 4 = extreme)
export function uvBand(uv: MaybeNumber) {
    if (!hasValue(uv)) {
        return 0;
    }

    if (uv < 3) return 0;
    if (uv < 6) return 1;
    if (uv < 8) return 2;
    if (uv < 11) return 3;
    return 4;
}

// Clamps value to [0, 1] relative to the given range.
export function ratio(value: MaybeNumber, min: number, max: number) {
    if (!hasValue(value)) {
        return 0;
    }

    return Math.min(1, Math.max(0, (value - min) / (max - min)));
}
